/* eslint-disable @typescript-eslint/no-non-null-assertion */
import * as React from "react";
import { FormControlLabel, Switch, Tooltip } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { SiteInfo } from "./SiteAboutPageUtils/siteAboutInterfaces";
import { ScheduleGrid } from "./ScheduleGrid";
import {
  ScheduleOverridesNotice,
  useScheduleOverrides,
} from "./ScheduleOverridesNotice";
import { EditScheduleTimesDialog } from "./EditScheduleTimesDialog";
import { useNavigate } from "react-router-dom";
import { OrangeTextButton } from "../../../common/OrangeTextButton";
import { SchedulesTabs } from "./SchedulesTabs";
import { SchedulesTab } from "./SchedulesTab";
import EditIcon from "@mui/icons-material/Edit";
import { EditActiveProductsDialog } from "./EditActiveProductsDialog";
import { EditScheduleNameDialog } from "./EditScheduleNameDialog";
// import { handleFlexSchedulingToggle } from "./SiteAboutPageUtils/handleFlexSchedulingToggle";
import BackdropLoadingScreen from "../../../common/BackdropLoadingScreen";
import CancelIcon from "@mui/icons-material/Cancel";
import { DeleteScheduleDialog } from "./DeleteScheduleDialog";
import { handleCheckOverlappingSchedulesForSharedProducts } from "./SiteAboutPageUtils/handleCheckOverlappingSchedulesForSharedProducts";
import { useSiteAboutStore } from "./siteAboutStore/siteAboutStore";
import { SchedulesTabAutopatrol } from "./SchedulesTabAutopatrol";
import { FlexIgnoreZonesOnboardingDialog } from "./FlexIgnoreZonesOnboarding/FlexIgnoreZonesOnboardingDialog";
import { FlexIgnoreZonesOffboardingDialog } from "./FlexIgnoreZonesOnboarding/FlexIgnoreZonesOffboardingDialog";
import { useFlexIgnoreZonesOnboardingStore } from "./FlexIgnoreZonesOnboarding/flexIgnoreZonesOnboardingStore";
import {
  FLEX_IZ_LD_EMPTY_ENABLED_BY_SITE,
  FLEX_IZ_LD_EMPTY_SYNC_LOADING,
} from "./FlexIgnoreZonesOnboarding/flexIgnoreZonesLdEmpty";
import { FlexIgnoreZonesScheduleEditPresetSection } from "./FlexIgnoreZonesOnboarding/FlexIgnoreZonesScheduleEditPresetSection";
import { useFeatureFlag } from "../../../../hooks/useFeatureFlag";
import { FlexCameraSummary } from "./FlexIgnoreZonesOnboarding/types";
import { isFleetSite } from "./FlexIgnoreZonesOnboarding/flexPresetConflicts";

export const SchedulesTabEditMode = ({
  siteInfo,
  setEditMode,
  setSiteInfo,
  setActiveTab,
}: {
  siteInfo: SiteInfo;
  setEditMode: React.Dispatch<React.SetStateAction<boolean>>;
  setSiteInfo: React.Dispatch<React.SetStateAction<SiteInfo>>;
  setActiveTab: React.Dispatch<React.SetStateAction<any>>;
}) => {
  const navigate = useNavigate();

  const {
    siteSchedules,
    schedulesIndex,
    editActiveProductsDialogOpen,
    setEditActiveProductsDialogOpen,
    editScheduleNameDialogOpen,
    setEditScheduleNameDialogOpen,
    flexSchedulesOn,
    deleteScheduleDialogOpen,
    setDeleteScheduleDialogOpen,
    schedulesBackdropLoadingScreenOn,
    handleFlexSchedulingToggle,
    handleAddSchedule,
  } = useSiteAboutStore();
  const { isFeatureEnabled } = useFeatureFlag();
  const showFlexIgnoreZones = isFeatureEnabled("showFlexIgnoreZones", false);
  const enabledBySite = useFlexIgnoreZonesOnboardingStore((state) =>
    showFlexIgnoreZones ? state.enabledBySite : FLEX_IZ_LD_EMPTY_ENABLED_BY_SITE
  );
  const setEnabledForSite = useFlexIgnoreZonesOnboardingStore((state) => state.setEnabledForSite);
  const initializeFromSite = useFlexIgnoreZonesOnboardingStore((state) => state.initializeFromSite);
  const flexIgnoreZonesEnabledSyncLoadingBySite = useFlexIgnoreZonesOnboardingStore((state) =>
    showFlexIgnoreZones
      ? state.flexIgnoreZonesEnabledSyncLoadingBySite
      : FLEX_IZ_LD_EMPTY_SYNC_LOADING
  );
  const [offboardingDialogOpen, setOffboardingDialogOpen] = React.useState(false);

  const handleCloseOffboardingDialog = () => {
    setOffboardingDialogOpen(false);
    window.dispatchEvent(new CustomEvent("refreshSiteCamerasTable"));
  };

  const userInfo = JSON.parse(localStorage.userInfo);

  const permissions = userInfo.permission;

  const isSuperUser =
    userInfo.username.includes("@aegissystems") ||
    userInfo.username.includes("@actuate") ||
    userInfo.email.includes("@aegissystems") ||
    userInfo.email.includes("@actuate");

  const siteCameras = JSON.parse(localStorage.siteCameras || '{"results": []}');
  const flexIgnoreZonesSyncLoading =
    flexIgnoreZonesEnabledSyncLoadingBySite[siteInfo.id] === true;
  const flexIgnoreZonesEnabled = enabledBySite[siteInfo.id] === true;

  const products = JSON.parse(localStorage.products);
  const productIds = new Map();

  products.forEach((product: any) => {
    productIds.set(product.id, product.name);
  });

  // ENG-2631: show overrides while editing too, so a holiday that replaces
  // the week being edited isn't a surprise.
  const { overrides, todayIso } = useScheduleOverrides(
    siteInfo.id,
    siteInfo.timezone
  );

  if (siteInfo.integration_type_name === "autopatrol") {
    return <SchedulesTabAutopatrol editMode={true} />;
  }

  const fullSchedulesTab = (
    <div className="pb-[10px]">
      <BackdropLoadingScreen openState={schedulesBackdropLoadingScreenOn} />
      <div className="border-t border-gray-200"></div>
      <ScheduleOverridesNotice overrides={overrides} todayIso={todayIso} />
      {editActiveProductsDialogOpen && <EditActiveProductsDialog />}
      {editScheduleNameDialogOpen && <EditScheduleNameDialog />}
      {showFlexIgnoreZones && (
        <FlexIgnoreZonesOnboardingDialog
          isFleetSite={isFleetSite(siteInfo)}
          onClose={() => {
            // Intentionally no-op, dialog store handles close lifecycle.
          }}
          onSuccessfulSubmit={() => {
            setEditMode(false);
            setActiveTab(<SchedulesTab siteInfo={siteInfo} />);
          }}
        />
      )}
      {showFlexIgnoreZones && (
        <FlexIgnoreZonesOffboardingDialog
          open={offboardingDialogOpen}
          siteId={siteInfo.id}
          currentScheduleId={siteSchedules[schedulesIndex]?.id}
          cameras={(siteCameras.results || []).map((camera: any) => ({
            id: Number(camera.id),
            camera_name: camera.camera_name,
            preview_url: camera.preview_url,
            ignore_zones: camera.ignore_zones,
          })) as FlexCameraSummary[]}
          onClose={handleCloseOffboardingDialog}
          onSubmitted={() => {
            setEnabledForSite(siteInfo.id, false);
            setEditMode(false);
            setActiveTab(<SchedulesTab siteInfo={siteInfo} />);
          }}
        />
      )}
      {deleteScheduleDialogOpen && (
        <DeleteScheduleDialog
          scheduleName={siteSchedules[schedulesIndex]?.display_name}
          scheduleId={siteSchedules[schedulesIndex]?.id}
        />
      )}
      <div
        className={`relative top-[0px] ${
          !flexSchedulesOn
            ? "h-[460]"
            : siteSchedules.length >= 2
            ? "h-[560px]"
            : "h-[540px]"
        } bg-white overflow-scroll`}
      >
        <div className="flex w-full items-center justify-between pr-3">
          <div className="flex items-center flex-wrap">
            <p className="font-black font-mulish text-[20px] p-3 indent-4">
              Schedule Overview &nbsp;
              <Tooltip
                title={`Scheduled arming time ranges are highlighted in green.`}
                placement="top"
                sx={{ fontSize: "15px", fontFamily: "mulish" }}
              >
                <InfoOutlinedIcon fontSize="small"></InfoOutlinedIcon>
              </Tooltip>
            </p>
            {permissions.flex_schedule && (
              <div className="pl-[30px] pt-[10px] flex flex-wrap">
                <FormControlLabel
                  sx={{ fontFamily: "Mulish" }}
                  control={
                    <Switch
                      color="warning"
                      checked={flexSchedulesOn}
                      disabled={false}
                      onChange={(event) => {
                        handleFlexSchedulingToggle(
                          event.target.checked,
                          setSiteInfo,
                          setEditMode,
                          setActiveTab,
                          siteInfo,
                          navigate
                        );
                      }}
                    />
                  }
                  label="Flex Scheduling"
                />
                {showFlexIgnoreZones && flexSchedulesOn && (
                  <FormControlLabel
                    sx={{ fontFamily: "Mulish" }}
                    control={
                      <Switch
                        color="warning"
                        checked={flexIgnoreZonesEnabled}
                        disabled={flexIgnoreZonesSyncLoading}
                        onChange={(event) => {
                          const enabled = event.target.checked;
                          if (enabled) {
                            setEnabledForSite(siteInfo.id, true);
                            initializeFromSite({
                              siteId: siteInfo.id,
                              schedules: siteSchedules.map((schedule: any, index: number) => ({
                                id: Number(schedule.id ?? index + 1),
                                display_name:
                                  schedule.display_name || `Schedule ${index + 1}`,
                              })),
                              cameras: (siteCameras.results || []).map((camera: any) => ({
                                id: Number(camera.id),
                                camera_name: camera.camera_name,
                                preview_url: camera.preview_url,
                                ignore_zones: camera.ignore_zones,
                              })),
                            });
                          } else {
                            setOffboardingDialogOpen(true);
                          }
                        }}
                      />
                    }
                    label="Flex Ignore Zones"
                  />
                )}
              </div>
            )}
          </div>
          {siteSchedules.length < 3 &&
            flexSchedulesOn &&
            permissions.flex_schedule && (
              <div className="shrink-0 pt-[15px]">
                <OrangeTextButton
                  onClick={() => {
                    handleAddSchedule(navigate);
                  }}
                  text="+ Add Schedule"
                  disabled={false}
                />
              </div>
            )}
        </div>
        <div className="pb-[10px]">
          {siteSchedules.length >= 2 &&
            flexSchedulesOn &&
            permissions.flex_schedule && <SchedulesTabs />}
        </div>
        {flexSchedulesOn && permissions.flex_schedule && (
          <div className="flex">
            <div className="pl-[30px] pb-[10px] overflow-scroll flex">
              <div className="font-black font-mulish text-[20px] flex items-center">
                {siteSchedules[schedulesIndex]?.display_name
                  ? siteSchedules[schedulesIndex]?.display_name
                  : `Schedule ${schedulesIndex + 1}`}
                {isSuperUser && (
                  <div className="pl-[10px] pb-[px]">
                    <Tooltip
                      title={
                        siteSchedules[schedulesIndex]?.log_name
                          ? `${siteSchedules[schedulesIndex].log_name}`
                          : ""
                      }
                      placement="top"
                      sx={{ fontSize: "15px", fontFamily: "mulish" }}
                    >
                      <InfoOutlinedIcon fontSize="small"></InfoOutlinedIcon>
                    </Tooltip>
                  </div>
                )}
              </div>
              <div className="relative top-[-5px]">
                <OrangeTextButton
                  onClick={() => {
                    setEditScheduleNameDialogOpen(true);
                  }}
                  text={
                    <Tooltip
                      title={"Edit Schedule Name"}
                      placement="top"
                      sx={{ fontSize: "20px", fontFamily: "mulish" }}
                    >
                      <EditIcon />
                    </Tooltip>
                  }
                  disabled={false}
                />
              </div>
              <div className="flex">
                <p className="font-black font-mulish text-[16px] pl-[30px] pt-[5px]">
                  {`Active Products: `}
                </p>
                <div className="pl-[6px] text-[16px] pt-[5px]">
                  {siteSchedules[schedulesIndex]?.product ? (
                    `${siteSchedules[schedulesIndex].product
                      .map((product: number) => {
                        return `${productIds.get(product)}`;
                      })
                      .join(", ")}`
                  ) : (
                    <></>
                  )}
                </div>
                <div className="relative top-[-5px]">
                  <OrangeTextButton
                    onClick={() => {
                      setEditActiveProductsDialogOpen(true);
                    }}
                    text={
                      <Tooltip
                        title={"Edit Active Products"}
                        placement="top"
                        sx={{ fontSize: "20px", fontFamily: "mulish" }}
                      >
                        <EditIcon />
                      </Tooltip>
                    }
                    disabled={false}
                  />
                </div>
                {siteSchedules.length > 1 && (
                  <div className="pl-[30px]">
                    <OrangeTextButton
                      onClick={() => setDeleteScheduleDialogOpen(true)}
                      text={
                        <>
                          <CancelIcon fontSize="small" />
                          &nbsp;Delete Schedule
                        </>
                      }
                      disabled={false}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        <div className="relative top-[0px] left-[15px] w-[1500px]">
          <ScheduleGrid />
        </div>
      </div>
      {showFlexIgnoreZones && flexSchedulesOn && flexIgnoreZonesEnabled && (
        <FlexIgnoreZonesScheduleEditPresetSection
          siteInfo={siteInfo}
          showFlexIgnoreZones={showFlexIgnoreZones}
          flexIgnoreZonesEnabled={flexIgnoreZonesEnabled}
          flexSchedulesOn={flexSchedulesOn}
        />
      )}
    </div>
  );

  const remoteLinkSchedulesTab = (
    <div className="pb-[10px]">
      <div className="border-t border-gray-200"></div>
      <div className="relative top-[0px] h-[60px] bg-white overflow-scroll">
        <div className="flex">
          <p className="font-mulish text-[18px] p-3 text-center w-full">
            Remote Link enabled. See IMMIX for site schedule.
          </p>
        </div>
      </div>
    </div>
  );

  return siteInfo.has_remote_link ? remoteLinkSchedulesTab : fullSchedulesTab;
};
